import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
} from "@material-ui/core";
import { CloudUpload, InsertDriveFile, Delete } from "@material-ui/icons";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import Toast from "./toast";

function isLessThanOneMB(size) {
  return size < 1024 * 1024; // 1,048,576
}
function calculateFileSize(size) {
  return isLessThanOneMB(size)
    ? `${Math.round(size / 1024)} KB`
    : `${Math.round(size / (1024 * 1024))} MB`;
}

export default function FileUpload({
  onHandleAction,
  title = "Subir",
  hasExample,
  linkToDownloadExample,
  maxFiles = 1,
  accept = {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel": [".xls"],
    "text/csv": [".csv"],
  },
  multiple = false,
}) {
  const [files, setFiles] = useState([]);
  const [toast, setToast] = useState({ open: false, message: "" });

  const handleAction = async (files) => {
    await onHandleAction(files);
    setFiles([]);
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
      }));
      // console.log(acceptedFiles);
      if (!acceptedFiles.length) return;
      if (maxFiles > 1) {
        setFiles((prev) => [...prev, ...newFiles]);
        return;
      }

      setFiles([...newFiles]);
    },
    [files.length]
  );
  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxFiles,
      accept,
      multiple,
    });

  useEffect(() => {
    if (!fileRejections.length) return;
    const errorCode = fileRejections[0].errors[0].code;
    let errorMessage = "";
    switch (errorCode) {
      case "too-many-files": {
        errorMessage = `Solo se admite ${maxFiles} elemento${
          maxFiles > 1 ? "s" : ""
        }.`;
        break;
      }
      case "file-invalid-type": {
        errorMessage = `Solo se admite archivos con extension  ${Object.values(
          accept
        ).join(",")}.`;
        break;
      }

      default:
        break;
    }
    setToast({ open: true, message: errorMessage });
  }, [fileRejections]);

  const handleDelete = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box p={2} pt={0}>
      <Toast
        open={toast.open}
        message={toast.message}
        close={() => setToast({ open: false, message: "" })}
      />
      {hasExample && (
        <Box style={{ display: "flex", justifyContent: "end" }}>
          <Button
            component="a"
            href={linkToDownloadExample}
            target="_blank"
            rel="noopener noreferrer"
            download={true}
            variant={"outlined"}
            color={"default"}
            style={{ marginBottom: 16 }}
            startIcon={<GetAppRoundedIcon />}
            size="small"
          >
            Descargar ejemplo
          </Button>
        </Box>
      )}
      {/* Zona Drag & Drop */}
      <Paper
        {...getRootProps()}
        variant="outlined"
        style={{
          border: "2px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          marginBottom: "20px",
          backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload fontSize="large" color="action" />
        <Typography variant="body1">
          {isDragActive
            ? "Suelte tus archivos aquí"
            : "Suelte tus archivos aquí o busca en tu dispositivo"}
        </Typography>
        <Typography variant="caption" color="textPrimary">
          {` Solo se aceptan ${Object.values(accept).join(",")}`}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          Archivos de hasta 1 GB
        </Typography>
      </Paper>

      {/* Lista de archivos */}
      <List style={{ maxHeight: 400, overflowY: "auto" }}>
        {files.map((item, index) => {
          return (
            <ListItem key={index} divider>
              <ListItemIcon>
                <InsertDriveFile />
              </ListItemIcon>
              <ListItemText
                primary={item.file.name}
                secondary={`${calculateFileSize(item.file.size)}`}
              />
              <IconButton onClick={() => handleDelete(index)}>
                <Delete />
              </IconButton>
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="contained"
        color="primary"
        disabled={!files.length}
        fullWidth
        style={{ marginBlock: 12 }}
        onClick={() => handleAction(files)}
      >
        {title}
      </Button>
    </Box>
  );
}
